import {
  Activity,
  Archive,
  Database,
  Globe,
  HardDrive,
  Server,
  Zap,
} from "lucide-react";
import React from "react";
import { BundledServiceEntry } from "../../../types/service-catalog";

// ── Icon resolver ─────────────────────────────────────────────────────────────

function getBundledServiceIcon(entry: BundledServiceEntry): React.ComponentType<{ className?: string }> {
  const t = (entry.type || entry.name || "").toLowerCase();
  if (t.startsWith("database")) return Database;
  if (t.includes("postgres") || t.includes("mysql") || t.includes("mongo")) return Database;
  if (t.includes("redis") || t.includes("cache")) return Zap;
  if (t === "object-storage" || t.includes("minio") || t.includes("storage")) return Archive;
  if (t === "observability" || t.includes("monitor")) return Activity;
  if (t.startsWith("web")) return Globe;
  if (t.includes("storage") || t.includes("volume")) return HardDrive;
  return Server;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface BundledServicesListProps {
  bundledServices?: BundledServiceEntry[] | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BundledServicesList({ bundledServices }: BundledServicesListProps) {
  if (!bundledServices || bundledServices.length === 0) return null;

  return (
    <div className="space-y-2.5">
      <p className="text-sm font-medium">What gets deployed</p>
      <div className="rounded-md bg-card divide-y divide-border">
        {bundledServices.map((entry, i) => {
          const Icon = getBundledServiceIcon(entry);
          return (
            <div key={i} className="flex items-start gap-3 px-3 py-2.5">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-sm font-medium leading-tight">
                    {entry.name}
                  </span>
                  {entry.version && (
                    <span className="text-2xs text-muted-foreground font-mono">
                      v{entry.version}
                    </span>
                  )}
                </div>
                {entry.purpose && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {entry.purpose}
                  </p>
                )}
                {entry.resourceHint && (
                  <p className="text-2xs text-muted-foreground/70 mt-0.5">
                    {entry.resourceHint}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
